import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import Table from '~components/molecules/Table';

import UtilFunctions from '~utils/functions';
import { BranchStatusEnum, ModuleEnum, PermissionsEnum } from '~utils/enums';

import * as Styles from './_branchesTable.module.scss';
import ActionButton from '~components/atoms/ActionButton';
import { TBranch } from '~types/feat/branch';

const BranchesTable = () => {
    const {
        Misc: {
            dateTimeFormat,
            renderBranchStatusChip,
        },
    } = UtilFunctions;

    const dispatch = useAppDispatch();

    const index = useAppSelector(state => state.branch.branchListing.index);
    const branchListing = useAppSelector(state => state.branch.branchListing.data);
    const isLoading = useAppSelector(state => state.branch.loadings.getBranchListing);

    const allRegions = useAppSelector(state => state.branch.allRegions);

    const userAccess = useAppSelector(state => state.auth.userDetails.access);
    const hasEditAccess = !!userAccess.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.BranchManagement);

    const setBranchDetailsModalIsOpen = (isOpen: boolean) => dispatch(combinedActions.branchSetBranchDetailsModalIsOpen(isOpen));
    const setSelectedBranch = (selectedBranch: TBranch) => dispatch(combinedActions.branchSetSelectedBranch(selectedBranch));
    const setIsEditing = (isEditMode: boolean) => dispatch(combinedActions.branchSetBranchDetailsModalIsEditing(isEditMode));
    const setDeleteModalIsOpen = (isOpen: boolean) => dispatch(combinedActions.branchSetDeleteBranchModalIsOpen(isOpen));

    useEffect(() => {
        dispatch(combinedActions.branchGetBranchListingAttempt());
        dispatch(combinedActions.branchGetAllRegionsAttempt());
    }, [index]);

    const handleViewBranchDetailsClick = (branch: TBranch) => {
        setBranchDetailsModalIsOpen(true);
        setSelectedBranch(branch);
    };

    const handleEditBranchClick = (branch: TBranch) => {
        setIsEditing(true);
        setBranchDetailsModalIsOpen(true);
        setSelectedBranch(branch);
    };

    const handleDeleteBranchClick = (branch: TBranch) => {
        setSelectedBranch(branch);
        setDeleteModalIsOpen(true);
    };

    const header = [
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'STATUS',
        'CREATED AT',
        'ACTION',
    ];

    const body = branchListing.map(region => {
        const {
            regionId,
            branchId,
            branchCode,
            branchName,
            status,
            createdAt,
        } = region;

        const regionName = allRegions?.find(item => item.regionId === regionId)?.regionName;

        const actionButtons = (
            <div
                key={branchId}
                className={Styles['button-container']}
            >
                <div>

                    <ActionButton
                        label='View'
                        onClick={() => handleViewBranchDetailsClick(region)}
                    />

                    <>
                        {(hasEditAccess && status !== BranchStatusEnum.Deleted)
                        &&
                         (
                             <ActionButton
                                 label='Edit'
                                 onClick={() => handleEditBranchClick(region)}
                             />
                         )
                        }

                        {(hasEditAccess && status === BranchStatusEnum.Active)
                        && (
                            <ActionButton
                                label='Delete'
                                onClick={() => handleDeleteBranchClick(region)}
                                className={Styles.danger}
                            />
                        )
                        }
                    </>
                </div>
            </div>
        );

        return [
            branchCode,
            branchName,
            regionName,
            renderBranchStatusChip(status, BranchStatusEnum[status]) || '-',
            dateTimeFormat(createdAt),
            actionButtons,
        ];
    });

    return (
        <Table
            header={header}
            body={body}
            isLoading={isLoading}
        />
    );
};

export default BranchesTable;
